import React, { createContext, useContext, useMemo, useState } from 'react';
import { createTheme, ThemeProvider } from '@mui/material';

type Mode = 'light' | 'dark';

interface ColorModeContextValue {
  mode: Mode;
  toggleMode: () => void;
}

const ColorModeContext = createContext<ColorModeContextValue>({
  mode: 'light',
  toggleMode: () => {},
});

const buildTheme = (mode: Mode) =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: '#08233f',
        light: '#123b63',
        dark: '#031427',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#ffc928',
        light: '#ffe17a',
        dark: '#d99b00',
        contrastText: '#07182d',
      },
      success: { main: '#14a35b' },
      warning: { main: '#f3b51b' },
      background: {
        default: mode === 'dark' ? '#0d1b2a' : '#f5f7fb',
        paper: mode === 'dark' ? '#122236' : '#ffffff',
      },
      text: {
        primary: mode === 'dark' ? '#e8edf4' : '#07182d',
        secondary: mode === 'dark' ? '#8ba5bf' : '#667085',
      },
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: 'Inter, Poppins, system-ui, -apple-system, sans-serif',
      h1: { fontWeight: 800, letterSpacing: 0 },
      h2: { fontWeight: 800, letterSpacing: 0 },
      h3: { fontWeight: 800, letterSpacing: 0 },
      h4: { fontWeight: 800, letterSpacing: 0 },
      h5: { fontWeight: 800, letterSpacing: 0 },
      h6: { fontWeight: 700, letterSpacing: 0 },
      button: { textTransform: 'none', fontWeight: 700, letterSpacing: 0 },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 8, boxShadow: 'none', paddingLeft: 18, paddingRight: 18 },
          containedSecondary: { color: '#07182d' },
        },
      },
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiCard: {
        styleOverrides: { root: { borderRadius: 8 } },
      },
      MuiChip: {
        styleOverrides: { root: { fontWeight: 700 } },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined' },
      },
    },
  });

export const ColorModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<Mode>(() => {
    const saved = localStorage.getItem('color_mode');
    return (saved === 'dark' || saved === 'light') ? saved : 'light';
  });

  const toggleMode = () => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('color_mode', next);
      return next;
    });
  };

  const theme = useMemo(() => buildTheme(mode), [mode]);
  const value = useMemo(() => ({ mode, toggleMode }), [mode]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export const useColorMode = () => useContext(ColorModeContext);
